import { h } from "./../../utils/createElement";
import { __assign, __extends } from "tslib";
import * as React from 'react';
import ReactDOM from 'react-dom';
import * as PropTypes from 'prop-types';
import Trigger from '../../utils/Trigger';
import { emptyFn } from '../../utils/helper';
import { consumeTheme } from '../../utils/theme';
import { ContextMenuContext, getContextMenuContext } from './context';
import throttle from 'lodash/throttle';
var DEFAULT_METRICS = {
    horizontal: 'right',
    vertical: 'bottom',
    height: 0
};
var ContextMenu = (function (_super) {
    __extends(ContextMenu, _super);
    function ContextMenu() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.popupRef = React.createRef();
        _this.anchorRef = React.createRef();
        _this.state = {
            visible: false,
            menu: null,
            location: {
                left: 0,
                top: 0
            },
            metrics: __assign({}, DEFAULT_METRICS)
        };
        _this.handleScroll = throttle(function (e) {
            var _a;
            if ((_a = _this.popupRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
                return;
            }
            _this.hide();
        }, 30);
        _this.handleContextMenu = function (e) {
            e.preventDefault();
            var _a = _this.props, menu = _a.menu, disabled = _a.disabled, onVisibleChange = _a.onVisibleChange;
            if (disabled) {
                return;
            }
            _this.setState({
                menu: typeof menu === 'function' ? menu(e) : menu,
                visible: false,
                location: { left: e.pageX, top: e.pageY },
                metrics: __assign(__assign({}, _this.state.metrics), { height: 0 })
            }, function () {
                _this.updatePopoutMetrics();
                _this.setState({ visible: true }, function () {
                    onVisibleChange(true);
                });
            });
        };
        _this.hide = function () {
            if (!_this.isVisible()) {
                return;
            }
            _this.setState({ visible: false }, function () {
                _this.props.onVisibleChange(false);
            });
        };
        _this.themedRender = function (classNames) {
            var _a;
            var _b = _this.props, prefixCls = _b.prefixCls, className = _b.className, style = _b.style, children = _b.children, disabled = _b.disabled, popupStyle = _b.popupStyle, zIndex = _b.zIndex, popupClassName = _b.popupClassName;
            var visible = _this.isVisible();
            var cls = classNames(prefixCls, className, (_a = {},
                _a[prefixCls + "-disabled"] = disabled,
                _a));
            var _c = _this.state, _d = _c.location, left = _d.left, top = _d.top, menu = _c.menu, _e = _c.metrics, height = _e.height, vertical = _e.vertical, horizontal = _e.horizontal;
            var popupCls = classNames(prefixCls + "-popup", popupClassName, prefixCls + "-popup-" + horizontal + "-" + vertical, prefixCls + "-popup-" + (visible ? 'visible' : 'hidden'));
            return (h(Trigger, { action: _this.hide },
                h("div", { className: cls, style: style, onContextMenu: _this.handleContextMenu, ref: _this.anchorRef }, children),
                ReactDOM.createPortal(h(Trigger, { className: popupCls, style: __assign({ left: left + "px", top: top + "px", height: height ? height + "px" : undefined, zIndex: zIndex }, popupStyle), action: _this.hide, domRef: _this.popupRef, disableContextMenu: true },
                    h(ContextMenuContext.Provider, { value: getContextMenuContext({ visible: visible, onClick: _this.hide }) }, menu)), document.body)));
        };
        return _this;
    }
    ContextMenu.getDerivedStateFromProps = function (props, state) {
        if (props.disabled) {
            return { visible: false };
        }
        return null;
    };
    ContextMenu.prototype.componentDidMount = function () {
        document.addEventListener('scroll', this.handleScroll, { passive: true, capture: true });
        document.addEventListener('wheel', this.handleScroll);
    };
    ContextMenu.prototype.componentWillUnmount = function () {
        document.removeEventListener('scroll', this.handleScroll);
        document.removeEventListener('wheel', this.handleScroll);
    };
    ContextMenu.prototype.isVisible = function () {
        var _a = this.props, forceVisible = _a.visible, disabled = _a.disabled;
        if (disabled) {
            return false;
        }
        var stateVisible = this.state.visible;
        return typeof forceVisible === 'boolean' ? forceVisible : stateVisible;
    };
    ContextMenu.prototype.updatePopoutMetrics = function () {
        var _a = this.props, popupBoundary = _a.popupBoundary, popupMargin = _a.popupMargin;
        var anchorDom = this.anchorRef.current;
        var popupDom = this.popupRef.current;
        if (!popupDom || !anchorDom) {
            return;
        }
        var _b = this.state.location, left = _b.left, top = _b.top;
        var targetLeft = left - window.pageXOffset;
        var targetTop = top - window.pageYOffset;
        var maxLeft = 0, maxRight = window.innerWidth, maxTop = 0, maxBottom = window.innerHeight;
        var _c = anchorDom.getBoundingClientRect(), containerLeft = _c.left, containerRight = _c.right, containerTop = _c.top, containerBottom = _c.bottom;
        if (popupBoundary === 'container') {
            maxLeft = Math.max(maxLeft, containerLeft);
            maxRight = Math.min(maxRight, containerRight);
            maxTop = Math.max(maxTop, containerTop);
            maxBottom = Math.min(maxBottom, containerBottom);
        }
        maxLeft += popupMargin;
        maxRight -= popupMargin;
        maxTop += popupMargin;
        maxBottom -= popupMargin;
        var _d = popupDom.getBoundingClientRect(), width = _d.width, height = _d.height;
        var horizontal = (targetLeft + width <= maxRight) ?
            'right'
            : (targetLeft <= (maxRight + maxLeft) / 2) ?
                'right'
                : 'left';
        var remainingBottom = maxBottom - targetTop, remainingTop = targetTop - maxTop;
        var vertical = 'bottom';
        var forcedHeight = 0;
        if (height > remainingBottom) {
            if (height > remainingTop) {
                if (remainingBottom >= remainingTop) {
                    vertical = 'bottom';
                    forcedHeight = remainingBottom;
                }
                else {
                    vertical = 'top';
                    forcedHeight = remainingTop;
                }
            }
            else {
                vertical = 'top';
            }
        }
        else {
            vertical = 'bottom';
        }
        var result = {
            horizontal: horizontal,
            vertical: vertical,
            height: forcedHeight
        };
        this.setState({ metrics: result });
    };
    ContextMenu.prototype.render = function () {
        return consumeTheme(this.themedRender);
    };
    return ContextMenu;
}(React.Component));
export default ContextMenu;
ContextMenu.defaultProps = {
    prefixCls: 'dui-menu-contextmenu',
    disabled: false,
    popupBoundary: 'viewport',
    popupMargin: 0,
    onVisibleChange: emptyFn
};
ContextMenu.propTypes = {
    prefixCls: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    menu: PropTypes.any,
    disabled: PropTypes.bool,
    popupStyle: PropTypes.object,
    popupClassName: PropTypes.string,
    visible: PropTypes.bool,
    popupBoundary: PropTypes.oneOf(['container', 'viewport']),
    popupMargin: PropTypes.number,
    onVisibleChange: PropTypes.func,
    zIndex: PropTypes.number
};
